import { z } from 'zod'

import { apiSuccess, parseApiResponse } from '@services/apiSchemas'
import { operatorApiFetch } from './apiService'

// ── 只有 Confirm 用到的 show / confirm 兩個端點做精確 Zod 邊界驗證；其餘端點維持寬鬆
//    envelope（schema passthrough = forward-compat，見 @services/apiSchemas）。 ──

// show（getById）：只 assert Confirm 會讀的欄位，其餘 passthrough（forward-compat）。
const PickingListItemSchema = z
  .object({
    id: z.string(),
    quantity: z.number(),
    picked_quantity: z.number().nullable().optional(),
    sku: z.string().nullable().optional(),
    product_name: z.string().nullable().optional(),
  })
  .passthrough()

const ShelfAllocationSchema = z
  .object({
    id: z.string(),
    picking_list_item_id: z.string(),
    shelf_location: z.string().nullable().optional(),
    quantity: z.number(),
  })
  .passthrough()

const PickingListDetailSchema = z
  .object({
    display_name: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    picking_list_items: z.array(PickingListItemSchema),
    // 只有 with_shelf_allocations=true 才回；Confirm 一律帶，仍標 optional 防呆。
    shelf_allocations: z.array(ShelfAllocationSchema).optional(),
  })
  .passthrough()

// confirm 成功 data：批次取號 jid（非波次最後一張 PL 時為 null）+ 波次名。
const ConfirmResultSchema = z
  .object({
    batch_tracking_job_id: z.string().nullable().optional(),
    picking_wave_name: z.string().optional(),
  })
  .passthrough()

export type PickingListDetail = z.infer<typeof PickingListDetailSchema>
export type ConfirmResult = z.infer<typeof ConfirmResultSchema>

// 未做精確 Zod 的端點回傳型別（寬鬆但非 any）。
interface OperatorApiEnvelope {
  success?: boolean
  message?: string
  data?: unknown
  has_more?: boolean
  interpolations?: Record<string, unknown>
  [key: string]: unknown
}

export const pickingListService = {
  // 列表
  getRecords: async (params: Record<string, unknown>): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch('picking_lists/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return response.json()
  },

  // 全選 ID
  getIds: async (params: Record<string, unknown>): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch('picking_lists/get_ids', {
      method: 'POST',
      body: JSON.stringify(params),
    })
    return response.json()
  },

  // 創建揀貨單
  create: async (data: Record<string, unknown>): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch('picking_lists', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.json()
  },

  // 匯出
  export: async (ids: string[]): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch('picking_lists/export', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
    return response.json()
  },

  // 批次下載所選揀貨單所屬波次的訂單附件（背景打包 ZIP）
  downloadAttachments: async (ids: string[]): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch('picking_lists/download_attachments', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
    return response.json()
  },

  // 匯出單一 PL 的揀貨紀錄（pick + put_back movements）
  exportPickHistory: async (pickingListId: string): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch(`picking_lists/${pickingListId}/export_pick_history`, {
      method: 'POST',
    })
    return response.json()
  },

  // 取得單一揀貨單。Confirm 紙本回報頁帶 withShelfAllocations 載入計畫儲位。
  getById: async (id: string, options: { withShelfAllocations?: boolean } = {}) => {
    const query = options.withShelfAllocations ? '?with_shelf_allocations=true' : ''
    const response = await operatorApiFetch(`picking_lists/${id}.json${query}`)
    return parseApiResponse(response, apiSuccess(PickingListDetailSchema))
  },

  // 取得 tote 殘留明細（含建議來源儲位）
  getToteItems: async (pickingListId: string): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch(`picking_lists/${pickingListId}/tote_items`)
    return response.json()
  },

  // 整批歸位 tote（動作 B，dynamic 倉由後端 reject）
  putBackTote: async (pickingListId: string, items: unknown): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch(`picking_lists/${pickingListId}/put_back_tote`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
    return response.json()
  },

  // 繼續揀貨（reopen）— 誤按 complete/terminate 後的救援
  reopen: async (pickingListId: string): Promise<OperatorApiEnvelope> => {
    const response = await operatorApiFetch(`picking_lists/${pickingListId}/reopen`, {
      method: 'POST',
    })
    return response.json()
  },

  // 紙本回報確認 + 實質移庫（mutating）。目前後端失敗回 400，operatorApiFetch 直接 throw
  // （錯誤交給 caller catch → handleNetworkError）；parseApiResponse 實際只 parse 200 成功 envelope。
  confirm: async (id: string, exceptions: unknown[]) => {
    const response = await operatorApiFetch(`picking_lists/${id}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exceptions }),
    })
    return parseApiResponse(response, apiSuccess(ConfirmResultSchema))
  },
}
export default pickingListService
